import { Manifest, StateFile } from '@/src/io/state-file/schema';
import { Maybe } from '@/src/types';
import type { AnnotationToolStore } from '@/src/store/tools/useAnnotationTool';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { useCrosshairsToolStore } from './crosshairs';
import { useCropStore } from './crop';
import { useCurvedMPRStore } from './curvedMPR';
import { usePaintToolStore } from './paint';
import { usePolygonStore } from './polygons';
import { useRectangleStore } from './rectangles';
import { useRulerStore } from './rulers';
import { useAngleStore } from './angles';
import { useArrowStore } from './arrows';
import { useCircleStore } from './circles';
import { useFreehandStore } from './freehands';
import { AnnotationToolType, IToolStore, Tools } from './types';
import { useViewStore } from '@/src/store/views';
import {
  EffectiveView,
  getEffectiveView,
} from '@/src/core/views/effectiveView';
import { isToolbarToolEnabled } from '@/src/features/featureFlags';

const CINE_DISALLOWED_TOOLS = new Set([
  Tools.Paint,
  Tools.Crop,
  Tools.Crosshairs,
]);

export function isToolAllowedFor(tool: Tools, effective: EffectiveView | null) {
  if (!isToolbarToolEnabled(tool)) return false;
  if (effective?.kind === 'cine' && CINE_DISALLOWED_TOOLS.has(tool))
    return false;
  return true;
}

const activeEffectiveView = () => getEffectiveView(useViewStore().activeView);

function coerceForEffective(tool: Tools, effective: EffectiveView | null) {
  return isToolAllowedFor(tool, effective) ? tool : Tools.Select;
}

function buildAnnotationToolStoreMap(): Record<
  AnnotationToolType,
  () => AnnotationToolStore
> {
  if (!__FEATURE_ANNOTATIONS__) {
    return {} as Record<AnnotationToolType, () => AnnotationToolStore>;
  }

  return {
    [AnnotationToolType.Polygon]: usePolygonStore,
    [AnnotationToolType.Rectangle]: useRectangleStore,
    [AnnotationToolType.Ruler]: useRulerStore,
    [AnnotationToolType.Angle]: useAngleStore,
    [AnnotationToolType.Arrow]: useArrowStore,
    [AnnotationToolType.Circle]: useCircleStore,
    [AnnotationToolType.Freehand]: useFreehandStore,
  };
}

export const AnnotationToolStoreMap = buildAnnotationToolStoreMap();

function buildToolStoreMap(): Record<Tools, Maybe<() => IToolStore>> {
  const map: Partial<Record<Tools, Maybe<() => IToolStore>>> = {
    [Tools.Pan]: null,
    [Tools.WindowLevel]: null,
    [Tools.Zoom]: null,
    [Tools.Select]: null,
    [Tools.Crosshairs]: useCrosshairsToolStore,
    [Tools.Crop]: null,
    [Tools.Paint]: usePaintToolStore,
    [Tools.Rectangle]: null,
    [Tools.Ruler]: null,
    [Tools.Polygon]: null,
    [Tools.Angle]: null,
    [Tools.Arrow]: null,
    [Tools.Circle]: null,
    [Tools.Freehand]: null,
    [Tools.CurvedMPRSpline]: null,
  };

  if (__FEATURE_CROPPING__) {
    map[Tools.Crop] = useCropStore;
  }

  if (__FEATURE_IMPLANT_PLANNING__) {
    map[Tools.CurvedMPRSpline] = useCurvedMPRStore;
  }

  if (__FEATURE_ANNOTATIONS__) {
    Object.assign(map, AnnotationToolStoreMap);
  }

  return map as Record<Tools, Maybe<() => IToolStore>>;
}

export const ToolStoreMap = buildToolStoreMap();

export function useAnnotationToolStore(
  type: AnnotationToolType
): AnnotationToolStore {
  const useStore = AnnotationToolStoreMap[type];
  return useStore();
}

function setupTool(tool: Tools) {
  const useStore = ToolStoreMap[tool];
  const store = useStore?.();
  if (store?.activateTool) {
    return store.activateTool?.();
  }
  return true;
}

function teardownTool(tool: Tools) {
  const useStore = ToolStoreMap[tool];
  const store = useStore?.();
  if (store) {
    store.deactivateTool?.();
  }
}

export const useToolStore = defineStore('tool', () => {
  const currentTool = ref(Tools.WindowLevel);
  const toolBeforeTemporaryCrosshairs = ref<Tools>(currentTool.value);

  function setCurrentTool(tool: Tools) {
    const coerced = coerceForEffective(tool, activeEffectiveView());
    if (currentTool.value === coerced) {
      return;
    }
    if (!setupTool(coerced)) {
      return;
    }
    teardownTool(currentTool.value);
    currentTool.value = coerced;
  }

  function activateTemporaryCrosshairs() {
    if (!isToolAllowedFor(Tools.Crosshairs, activeEffectiveView())) return;
    toolBeforeTemporaryCrosshairs.value = currentTool.value;
    setCurrentTool(Tools.Crosshairs);
    useCrosshairsToolStore().setDragging(true);
  }

  function deactivateTemporaryCrosshairs() {
    useCrosshairsToolStore().setDragging(false);
    setCurrentTool(toolBeforeTemporaryCrosshairs.value);
  }

  watch(
    () => activeEffectiveView()?.kind ?? null,
    () => {
      const eff = activeEffectiveView();
      const coerced = coerceForEffective(currentTool.value, eff);
      if (coerced !== currentTool.value) {
        teardownTool(currentTool.value);
        currentTool.value = coerced;
      }
    }
  );

  function serialize(state: StateFile) {
    const { tools } = state.manifest;
    if (!tools) return;

    Object.values(ToolStoreMap)
      .map((useStore) => useStore?.())
      .filter((store): store is IToolStore => !!store)
      .forEach((store) => {
        store.serialize?.(state);
      });

    tools.current = currentTool.value;
  }

  function deserialize(
    manifest: Manifest,
    segmentGroupIDMap: Record<string, string>,
    dataIDMap: Record<string, string>
  ) {
    usePaintToolStore().deserialize(manifest, segmentGroupIDMap);

    Object.values(ToolStoreMap)
      .filter((useStore) => useStore !== usePaintToolStore)
      .map((useStore) => useStore?.())
      .filter((store): store is IToolStore => !!store)
      .forEach((store) => {
        store.deserialize?.(manifest, dataIDMap);
      });

    if (manifest.tools?.current) {
      currentTool.value = coerceForEffective(
        manifest.tools.current,
        activeEffectiveView()
      );
    }
  }

  return {
    currentTool,
    setCurrentTool,
    serialize,
    deserialize,
    activateTemporaryCrosshairs,
    deactivateTemporaryCrosshairs,
  };
});
